import { useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { MoneyInput } from '@nucleus-commerce/admin-kit/forms';
import type { GiftCard } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { emptyAdjustForm, toGiftCardAdjust } from '../api/mappers';
import { useAdjustGiftCard } from '../hooks/use-gift-card-mutations';
import {
  ADJUST_DIRECTIONS,
  adjustGiftCardSchema,
  type AdjustGiftCardInput,
  type AdjustGiftCardOutput,
} from '../schemas/gift-card-form-schema';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['amount', 'note']);
const mapField = (field: string) => field.replace(/\.(currency|amount)$/, '');

export interface AdjustBalanceDialogProps {
  storeId: string;
  card: GiftCard;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A manual correction written to the ledger as an `adjust` row; the balance never goes below zero. */
export function AdjustBalanceDialog({
  storeId,
  card,
  open,
  onOpenChange,
}: AdjustBalanceDialogProps) {
  const { t } = useTranslation(['giftCards', 'common']);
  const format = useFormat();
  const adjust = useAdjustGiftCard(storeId);
  const message = useErrorMessage();
  const schema = useMemo(() => adjustGiftCardSchema(card.balance.amount), [card.balance.amount]);
  const form = useForm<AdjustGiftCardInput, unknown, AdjustGiftCardOutput>({
    resolver: useI18nResolver(schema),
    defaultValues: emptyAdjustForm(),
  });
  const [direction, amount] = useWatch({ control: form.control, name: ['direction', 'amount'] });
  const after =
    amount && amount.amount > 0
      ? card.balance.amount + (direction === 'remove' ? -amount.amount : amount.amount)
      : null;

  const close = () => {
    onOpenChange(false);
    adjust.reset();
    form.reset(emptyAdjustForm());
  };

  const submit = form.handleSubmit(async (values) => {
    try {
      await adjust.mutateAsync({ id: card.id, body: toGiftCardAdjust(values) });
      close();
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS, mapField })) return;
      form.setError('root.server', { type: 'server', message: message(error) });
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('giftCards:adjust.title')}</DialogTitle>
          <DialogDescription>
            {t('giftCards:adjust.description', { balance: format.money(card.balance) })}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="direction"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('giftCards:adjust.direction')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={(value) => {
                        field.onChange(value);
                        if (form.formState.isSubmitted) void form.trigger('amount');
                      }}
                      className="grid-cols-2 gap-2"
                    >
                      {ADJUST_DIRECTIONS.map((option) => (
                        <label
                          key={option}
                          htmlFor={`adjust-direction-${option}`}
                          className={cn(
                            'flex cursor-pointer items-center gap-2 rounded-md border p-3 text-sm',
                            field.value === option && 'border-primary bg-primary-tint/40',
                          )}
                        >
                          <RadioGroupItem id={`adjust-direction-${option}`} value={option} />
                          {t(`giftCards:adjust.directions.${option}`)}
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('giftCards:fields.amount')}</FormLabel>
                  <FormControl>
                    <MoneyInput
                      currency={card.balance.currency}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      aria-invalid={fieldState.invalid}
                      autoFocus
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {after !== null && after >= 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="adjust-preview">
                {t('giftCards:adjust.after', {
                  balance: format.money({ amount: after, currency: card.balance.currency }),
                })}
              </p>
            ) : null}
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('giftCards:adjust.note')}</FormLabel>
                  <FormControl>
                    <Textarea rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={close}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={adjust.isPending}>
                {adjust.isPending ? t('giftCards:adjust.submitting') : t('giftCards:adjust.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
